import {announce} from '@github-ui/aria-live'
import {useCallback, useEffect, useRef} from 'react'

import {TimelinePanelSkeletonRows} from './TimelinePanelSkeleton'

interface UseInfiniteScrollLoadProps {
  fetchNextPage: () => unknown
  hasMore: boolean
  isFetchingNextPage: boolean
}

export function useInfiniteScrollLoad({fetchNextPage, hasMore, isFetchingNextPage}: UseInfiniteScrollLoadProps) {
  const sentinelRef = useRef<HTMLDivElement>(null)

  const loadMore = useCallback(() => {
    if (hasMore && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [fetchNextPage, hasMore, isFetchingNextPage])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) loadMore()
      },
      {threshold: 0.1},
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [loadMore])

  useEffect(() => {
    if (isFetchingNextPage) announce('Loading more timeline events')
  }, [isFetchingNextPage])

  return hasMore ? (
    <div ref={sentinelRef} aria-hidden>
      <TimelinePanelSkeletonRows />
    </div>
  ) : undefined
}
